import { MeetingState } from './../types/meetings/meeting-status.type';
import { differenceInHours, getHours } from 'date-fns';
import { createDateFromNumberishDate } from '../helpers';
import { MeetingDetailDto, MeetingDto, MeetingRecipientDto } from '../interfaces/dtos/meetings';
import { Meeting, MeetingAction, MeetingDetail } from '../interfaces/meetings';
import { RegisterDetailDto, RegisterTargetMeetingDto } from '../interfaces/dtos/registers';
import { MeetingLight } from '../interfaces/meetings/meeting-light.interface';
import { ScheduleUtil } from './schedule.util';

export abstract class MeetingsMapper {
  private static determinePointActionRecipientKind(recipient: MeetingRecipientDto): 'user' | 'service' | 'group' {
    if (recipient.contactIdiAdmin || recipient.contactId) {
      return 'user';
    } else if (recipient.serviceId) {
      return 'service';
    } else if (recipient.groupeId) {
      return 'group';
    }

    return 'user';
  }

  public static toMeeting(meeting: MeetingDto): Meeting {
    return {
      id: meeting.id,
      presidentId: meeting.presidentId,
      secretaryId: meeting.secretaireId,
      locationId: meeting.lieuId,
      description: meeting.description,
      meetingTypeId: meeting.typeSeanceId,
      scheduleDetail: {
        meetingScheduledAt: meeting.reunionFixeeLe ? createDateFromNumberishDate(meeting.reunionFixeeLe) : null,
        startsAt: meeting.heureDebut,
        endsAt: meeting.heureFin,
        estimatedDurationInHours: meeting.dureeEstimeeHeures,
      },
      organizedBy: meeting.reunionFixeePar,
      locked: meeting.verrouille,
      deleted: meeting.supprime,
      serviceOrder: meeting.ordreService,
      foreColor: meeting.foreColor,
      nbrNonIncludedPoints: meeting.pointsNonInclusCount,
      nbrIncludedPoints: meeting.pointsInclusCount,
      nbrTtlPoints: (meeting.pointsInclusCount ?? 0) + (meeting.pointsNonInclusCount ?? 0),
    };
  }

  public static toMeetingDetail(meetingDetail: MeetingDetailDto): MeetingDetail {
    return {
      id: meetingDetail.id,
      description: meetingDetail.description,
      meetingTypeId: meetingDetail.typeSeanceId,
      streamingURL: meetingDetail.urlStreaming,
      scheduleDetail: {
        meetingScheduledAt: meetingDetail.date,
        startsAt: getHours(meetingDetail.heureDebut).toString(),
        endsAt: getHours(meetingDetail.heureFin).toString(),
        estimatedDurationInHours: differenceInHours(meetingDetail.heureDebut, meetingDetail.heureFin),
      },
      organizedBy: meetingDetail.reunionFixeePar,
      locked: meetingDetail.verrouille,
      deleted: meetingDetail.supprime,
      locationId: meetingDetail.lieuxId,
      nbrLinkedPoints: meetingDetail.pointsLinked,
    };
  }

  public static toMeetingLight(meeting: RegisterTargetMeetingDto | RegisterDetailDto): MeetingLight {
    const toMeetingState = (status: 'Ouvert' | 'Ferme' | 'Verrouille'): MeetingState => {
      switch (status) {
        case 'Ouvert':
          return 'open';
        case 'Ferme':
          return 'closed';
        case 'Verrouille':
          return 'locked';
        default:
          return 'open';
      }
    };

    const isRegisterDetailDto = (meeting: RegisterTargetMeetingDto | RegisterDetailDto): meeting is RegisterDetailDto =>
      'seanceId' in meeting;

    if (isRegisterDetailDto(meeting)) {
      return {
        id: meeting.seanceId,
        description: meeting.description,
        meetingType: '', //TODO
        location: meeting.lieu,
        date: meeting.date,
        status: toMeetingState(meeting.status),
        nbrPoints: meeting.nbrPoints,
      };
    }

    return {
      id: meeting.id,
      description: meeting.description,
      meetingType: meeting.typeSeanceLightVM.description,
      location: meeting.lieu!,
      date: meeting.date,
      status: toMeetingState(meeting.status),
      nbrPoints: meeting.nbrPoints,
      data: {
        disabled: !meeting.isModelFileGenerated,
      },
    };
  }

  public static toMeetingAction(dto: MeetingRecipientDto, meetingId: number): MeetingAction {
    const recipientKind = MeetingsMapper.determinePointActionRecipientKind(dto);

    return {
      scope: 'points',
      id: dto.id,
      meetingId,
      type: dto.traiteLe ? 'done' : 'to-do',
      mailId: dto.mailId,
      demander: {
        id: dto.transmissionUserId!,
        fromIadmin: true,
        kind: 'user',
      },
      recipient: {
        id:
          MeetingsMapper.determinePointActionRecipientKind(dto) === 'user'
            ? dto.contactIdiAdmin /*?? dto.contactId*/ // There is a difference between a contactIdAdmin and a contactId
            : MeetingsMapper.determinePointActionRecipientKind(dto) === 'service'
              ? dto.serviceId
              : dto.groupeId,
        fromIadmin: recipientKind === 'user',
        kind: recipientKind,
      },
      purpose: dto.action,
      subject: '',
      done: !!dto.traiteLe,
      doneAt: dto.traiteLe ? new Date(dto.traiteLe) : null,
      createdAt: new Date(dto.transmisDate!),
      delayedAnnouncement: ScheduleUtil.delaySinceToday(new Date(dto.transmisDate!)),
      status: dto.statut ?? null,
      deadline: dto.echeance ? new Date(dto.echeance) : null,
      message: dto.message,
      answer: dto.reponse,
      serviceId: dto.serviceId,
      groupId: dto.groupeId,
      processable: dto.rights.canReply,
    };
  }
}
